<script setup lang="ts">
    import { ref } from 'vue'
    import PkEditorMarkdown from './PkEditorMarkdown.vue'
    import PkStreamingMarkdown from './chat/PkStreamingMarkdown.vue'

    const props = defineProps<{
        label?: string
        readonly?: boolean
        disabled?: boolean
        placeholder?: string
        hintLabel?: string
        invalid?: boolean
        invalidLabel?: string | string[]
    }>()

    const modelValue = defineModel<string>()
    const isDialogVisible = ref(false)

    const localModelValue = ref<string>()
    const onClickEdit = () => {
        if (props.readonly || props.disabled) {
            return
        }
        localModelValue.value = modelValue.value
        isDialogVisible.value = true
    }
    const onClickSave = () => {
        modelValue.value = localModelValue.value || ''
        isDialogVisible.value = false
    }
</script>

<template>
    <div
        class="pk-editor-prompt"
        :class="{
            'pk-editor-prompt--disabled': disabled,
            'pk-editor-prompt--readonly': readonly,
            'pk-editor-prompt--invalid': invalid,
        }">
        <span v-if="label" class="pk-editor-prompt__label">
            {{ label }}
        </span>
        <div
            class="pk-editor-prompt__input light-scrollbar"
            @click.stop="onClickEdit">
            <div v-if="modelValue" class="my-auto">
                <PkStreamingMarkdown class="wysiwyg" :markdown="modelValue" />
            </div>
            <span v-else-if="placeholder" class="pk-editor-prompt__placeholder">
                {{ placeholder }}
            </span>
            <VvButton
                v-if="!readonly"
                class="pk-editor-prompt__edit"
                icon="ri:edit-line"
                modifiers="action-small"
                :disabled
                :title="$t('action.edit')"
                @click="onClickEdit" />
        </div>
        <small v-if="invalid" class="pk-editor-prompt__invalid-label">
            {{
                Array.isArray(invalidLabel)
                    ? invalidLabel.join(', ')
                    : invalidLabel
            }}
        </small>
        <small
            v-else-if="$slots.hint || hintLabel"
            class="pk-editor-prompt__hint">
            <slot name="hint">{{ hintLabel }}</slot>
        </small>
        <VvDialog
            v-model="isDialogVisible"
            :title="$t('action.edit')"
            size="large"
            keep-open>
            <PkEditorMarkdown
                v-model="localModelValue"
                class="mb-0"
                :readonly />
            <template #footer>
                <VvButtonGroup>
                    <VvButton
                        modifiers="secondary"
                        @click="isDialogVisible = false">
                        {{ $t('action.cancel') }}
                    </VvButton>
                    <VvButton @click="onClickSave">
                        {{ $t('action.save') }}
                    </VvButton>
                </VvButtonGroup>
            </template>
        </VvDialog>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-prompt {
            position: relative;
            margin-bottom: var(--input-gap);
            overflow: hidden;

            &.mb-0 {
                margin-bottom: 0;
            }

            &__label {
                color: var(--input-label-color);
                display: var(--input-label-display);
                font-size: var(--input-label-font-size);
                font-weight: var(--input-label-font-weight);
                margin-bottom: var(--input-label-gap);
            }

            &__input {
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: start;
                min-width: 0;
                background-color: var(--input-background-color);
                font-size: var(--input-font-size);
                padding: var(--input-spacing-top) var(--spacing-52)
                    var(--input-spacing-bottom) var(--input-spacing-left);
                min-height: var(--spacing-52);
                max-height: var(--spacing-192);
                line-height: var(--input-line-height);
                overflow: auto;
                cursor: pointer;
                border-bottom: 1px solid var(--color-word-5);
            }

            &__placeholder {
                color: var(--input-placeholder-color);
                margin-block: auto;
            }

            &__edit {
                position: absolute;
                right: var(--input-spacing-right);
                top: var(--spacing-14);
                z-index: 10;
            }

            &__hint {
                display: var(--input-hint-display);
                font-size: var(--input-hint-font-size);
                color: var(--input-hint-color);
                padding-top: var(--input-hint-gap);
                padding-inline: var(--spacing-xs);
                white-space: var(--input-hint-white-space);
                text-overflow: var(--input-hint-text-overflow);
                overflow: var(--input-hint-overflow);
            }

            &__invalid-label {
                display: var(--input-hint-display);
                font-size: var(--input-hint-font-size);
                color: var(--input-invalid-color);
                padding-top: var(--input-hint-gap);
                padding-inline: var(--spacing-xs);
                white-space: var(--input-hint-white-space);
                text-overflow: var(--input-hint-text-overflow);
                overflow: var(--input-hint-overflow);
            }

            &--readonly {
                .pk-editor-prompt__input {
                    cursor: default;
                    padding-right: var(--input-spacing-right);
                }
            }
            &--disabled {
                opacity: 0.5;

                .pk-editor-prompt__input {
                    cursor: not-allowed;
                }
            }

            &--invalid {
                .pk-editor-prompt__input {
                    border-color: var(--color-danger);
                    border-bottom-width: 2px;
                }
            }
        }
    }
</style>
